import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

// The rail's two ways of moving along: under a mouse, when it is wider than the window, it drifts so every garment can
// be reached without a scrollbar; on a phone it is swiped, and whichever garment reaches the middle turns to face the shopper.

/** How much of the distance left the rail covers each frame, which is what makes it glide after the mouse. */
const GLIDE = 0.09;

/** How far from its target the rail may rest. */
const SETTLED = 0.2;

// Reports whether the window has a real pointer that can hover, as a rail's mouse behaviour needs.
export const canHover = (): boolean => window.matchMedia('(hover: hover)').matches;

// Reports whether this shopper asked for less motion.
export const asksForStillness = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Glides a rail wider than its window along under the mouse, and answers how to bring one garment into view for a keyboard.
export function useRailDrift(view: RefObject<HTMLElement>, track: RefObject<HTMLElement>, on: boolean): (slot: HTMLElement) => void {
  const aim = useRef<(share: number) => void>(() => undefined);
  useEffect(() => {
    const box = view.current;
    const run = track.current;
    if (!on || !box || !run || !canHover()) return;
    let target = 0;
    let shown = 0;
    let frame = 0;
    // Moves the rail one step nearer its target, stopping once it has arrived.
    const step = () => {
      shown = asksForStillness() ? target : shown + (target - shown) * GLIDE;
      run.style.transform = `translate3d(${shown.toFixed(1)}px, 0, 0)`;
      frame = Math.abs(target - shown) > SETTLED ? requestAnimationFrame(step) : 0;
    };
    aim.current = (share) => {
      target = -Math.max(0, run.scrollWidth - box.clientWidth) * Math.min(1, Math.max(0, share));
      if (!frame) frame = requestAnimationFrame(step);
    };
    // Reads how far across the window the mouse is, as how far along the rail it should be.
    const move = (event: PointerEvent) => {
      const edge = box.getBoundingClientRect();
      aim.current((event.clientX - edge.left) / edge.width);
    };
    // A press stops the rail where it is: a garment gliding out from under the pointer between press and release would never receive the click.
    const hold = () => { target = shown; };
    box.addEventListener('pointermove', move, { passive: true });
    box.addEventListener('pointerdown', hold, { passive: true });
    return () => { box.removeEventListener('pointermove', move); box.removeEventListener('pointerdown', hold); cancelAnimationFrame(frame); run.style.transform = ''; aim.current = () => undefined; };
  }, [on, view, track]);
  return (slot) => {
    const run = track.current;
    if (run) aim.current((slot.offsetLeft + slot.offsetWidth / 2) / Math.max(1, run.scrollWidth));
  };
}

/** How far a mouse may travel between press and release and still be a press. */
const PRESS_SLOP = 8;

// Opens the garment a mouse pressed, whichever is under it when it lets go: garments part as one turns, so one can slide off the pointer mid-press, and the browser then sends no click at all.
export function useRailPress(onOpen: (at: number) => void) {
  const pressed = useRef<{ at: number; x: number; y: number } | null>(null);
  return {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => {
      const slot = (event.target as Element).closest('[data-rail-slot]');
      const at = slot ? Array.from(event.currentTarget.children).indexOf(slot) : -1;
      pressed.current = event.pointerType === 'mouse' && event.button === 0 && at >= 0 ? { at, x: event.clientX, y: event.clientY } : null;
    },
    onPointerUp: (event: ReactPointerEvent<HTMLElement>) => {
      const press = pressed.current;
      pressed.current = null;
      if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) < PRESS_SLOP) onOpen(press.at);
    }
  };
}

// Turns whichever garment reaches the middle of a swiped rail, on a screen with no pointer to hover it with.
export function useRailCentre(view: RefObject<HTMLElement>, on: boolean, onCentre: (at: number) => void): void {
  const told = useRef(onCentre);
  told.current = onCentre;
  useEffect(() => {
    const box = view.current;
    if (!on || !box || canHover()) return;
    let frame = 0;
    // Finds the garment nearest the middle and says so.
    const read = () => {
      frame = 0;
      const middle = box.getBoundingClientRect().left + box.clientWidth / 2;
      const slots = Array.from(box.querySelectorAll<HTMLElement>('[data-rail-slot]'));
      const distance = (slot: HTMLElement) => { const edge = slot.getBoundingClientRect(); return Math.abs(edge.left + edge.width / 2 - middle); };
      const nearest = slots.reduce((best, slot, at) => (distance(slot) < distance(slots[best]) ? at : best), 0);
      if (slots.length) told.current(nearest);
    };
    const scrolled = () => { if (!frame) frame = requestAnimationFrame(read); };
    read();
    box.addEventListener('scroll', scrolled, { passive: true });
    return () => { box.removeEventListener('scroll', scrolled); cancelAnimationFrame(frame); };
  }, [on, view]);
}
